import React, { useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { ArrowRightIcon } from '@heroicons/react/outline';
import { Link } from 'react-router-dom';
import { Button } from 'components/Button';
import { RequestStatusBadge } from 'components/RequestStatusBadge';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import {
  GET_HOME_BILLING_ACTIVITY,
  GET_HOME_ORDER_ACTIVITY,
} from 'gql/queries';
import { canManageRequests, MerchantRequest } from 'types/resolution';
import { userRoles } from 'types/user';
import { isShipaidAdmin } from 'utils';
import { withRequestReturnTo } from 'utils/requestNavigation';
import { getRequestTypeLabel } from 'utils/requestTypePresentation';
import { shipmentStatus } from './data/orders-data';

type ActivityTab = 'Requests' | 'Orders' | 'Billing';

interface HomeActivityProps {
  requests: MerchantRequest[];
  loading: boolean;
  ready: boolean;
}

interface OrderActivity {
  id: string;
  storeId: string;
  number: string;
  trackingStatus?: string | null;
  updatedAt?: string | null;
  items?: Array<{ deliveredAt?: string | null }>;
  activities?: Array<{ eventType: string; createdAt?: string | null }>;
}

interface InvoiceActivity {
  id: string;
  storeId: string;
  number: string;
  createdAt?: string | null;
}

interface ActivityQueryResult {
  recentOrders?: OrderActivity[];
  recentInvoices?: InvoiceActivity[];
}

const rowClassName =
  'flex flex-wrap items-center justify-between gap-3 px-5 py-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500';

const ActivityTime = ({ timestamp }: { timestamp?: string | null }) => {
  const date = timestamp ? new Date(timestamp) : null;
  return (
    <div className="flex items-center gap-3 text-xs text-gray-500">
      {date && !Number.isNaN(date.getTime()) ? (
        <time dateTime={timestamp || undefined}>
          Updated{' '}
          {date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </time>
      ) : (
        <span>Update time unavailable</span>
      )}
      <ArrowRightIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
    </div>
  );
};

const RequestActivity = ({ request }: { request: MerchantRequest }) => (
  <li>
    <Link
      to={withRequestReturnTo(
        `/${
          request.type === 'ISSUE' ? 'claims' : 'returns'
        }/${encodeURIComponent(request.id)}`,
        '/',
      )}
      className={rowClassName}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">
            {request.number}
          </span>
          <span className="text-xs text-gray-500">
            {getRequestTypeLabel(request)}
          </span>
          <RequestStatusBadge
            status={request.status}
            label={request.statusLabel}
          />
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 text-sm text-gray-500">
          {request.customerName && <span>{request.customerName}</span>}
          {request.orderNumber && <span>Order {request.orderNumber}</span>}
        </div>
      </div>
      <ActivityTime timestamp={request.updatedAt} />
    </Link>
  </li>
);

const orderDescription = (order: OrderActivity) => {
  const number = String(order.number).replace(/^#+/, '');
  const status = shipmentStatus.find(
    ({ value }) => value === order.trackingStatus,
  );
  const change =
    order.trackingStatus === 'DELIVERED'
      ? 'marked delivered'
      : order.trackingStatus
      ? `status changed to ${status?.label || order.trackingStatus}`
      : 'updated';
  return `Order #${number} ${change}`;
};

const AdditionalActivity = ({
  tab,
  storeId,
}: {
  tab: 'Orders' | 'Billing';
  storeId: string;
}) => {
  const { data, loading, error, refetch } = useQuery<ActivityQueryResult>(
    tab === 'Orders' ? GET_HOME_ORDER_ACTIVITY : GET_HOME_BILLING_ACTIVITY,
    {
      variables: { storeId, limit: 5 },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );
  const label = tab === 'Orders' ? 'order' : 'billing';
  const rows: Array<OrderActivity | InvoiceActivity> | undefined =
    tab === 'Orders' ? data?.recentOrders : data?.recentInvoices;
  // Retained responses must never be shown under a different selected store.
  const ready =
    !error &&
    Array.isArray(rows) &&
    rows.every((row) => row.storeId === storeId);

  if (loading) {
    return (
      <p role="status" className="p-5 text-sm text-gray-500">
        Loading {label} activity…
      </p>
    );
  }
  if (!ready) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 p-5">
        <p role="alert" className="text-sm text-red-700">
          {tab === 'Orders' ? 'Order' : 'Billing'} activity could not be loaded.
        </p>
        <Button
          variant="outlined"
          onClick={() => {
            void refetch().catch(() => undefined);
          }}
        >
          Retry {label} activity
        </Button>
      </div>
    );
  }

  const activities =
    tab === 'Orders'
      ? (data!.recentOrders || [])
          .filter(
            (order) => order.activities?.[0]?.eventType !== 'ORDER_CREATED',
          )
          .map((order) => ({
            id: order.id,
            text: orderDescription(order),
            href: withRequestReturnTo(
              `/orders/${encodeURIComponent(order.id)}`,
              '/',
            ),
            timestamp: order.activities?.[0]?.createdAt || order.updatedAt,
          }))
      : (data!.recentInvoices || []).map((invoice) => ({
          id: invoice.id,
          text: `Invoice ${invoice.number} issued`,
          href: `/settings/billing/invoices?invoice=${encodeURIComponent(
            invoice.number,
          )}`,
          timestamp: invoice.createdAt,
        }));

  if (!activities.length) {
    return (
      <p className="p-5 text-sm text-gray-500">
        {tab === 'Orders' ? 'No order updates yet.' : 'No invoices yet.'}
      </p>
    );
  }
  return (
    <ul className="divide-y divide-gray-100">
      {activities.slice(0, 5).map((activity) => (
        <li key={activity.id}>
          <Link to={activity.href} className={rowClassName}>
            <span className="min-w-0 text-sm font-medium text-gray-900">
              {activity.text}
            </span>
            <ActivityTime timestamp={activity.timestamp} />
          </Link>
        </li>
      ))}
    </ul>
  );
};

export const HomeActivity = ({
  requests,
  loading,
  ready,
}: HomeActivityProps) => {
  const { storeId } = useStore();
  const { role, user } = useUser();
  const canViewRequests = canManageRequests(role, isShipaidAdmin(user));
  const canViewBilling =
    role === userRoles.ADMIN ||
    role === userRoles.MANAGER ||
    role === userRoles.SHIPAID_ADMIN ||
    isShipaidAdmin(user);
  const tabs: ActivityTab[] = [];
  if (canViewRequests) tabs.push('Requests');
  if (canViewRequests || role === userRoles.VIEWER) tabs.push('Orders');
  if (canViewBilling) tabs.push('Billing');
  const [selectedTab, setSelectedTab] = useState<ActivityTab>('Requests');
  const activeTab = tabs.includes(selectedTab) ? selectedTab : tabs[0];
  const tabRefs = useRef<
    Partial<Record<ActivityTab, HTMLButtonElement | null>>
  >({});
  if (!tabs.length) return null;
  const viewAll =
    activeTab === 'Requests'
      ? {
          href: '/requests?group=ALL&sort=UPDATED_AT_DESC',
          label: 'View all requests',
        }
      : activeTab === 'Orders'
      ? { href: '/orders', label: 'View all orders' }
      : { href: '/settings/billing/invoices', label: 'View all invoices' };

  const navigateTabs = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    tab: ActivityTab,
  ) => {
    const index = tabs.indexOf(tab);
    let next: ActivityTab;
    switch (event.key) {
      case 'ArrowRight':
        next = tabs[(index + 1) % tabs.length];
        break;
      case 'ArrowLeft':
        next = tabs[(index - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    setSelectedTab(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section
      aria-labelledby="home-activity-title"
      className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <div>
          <h2
            id="home-activity-title"
            className="text-lg font-semibold text-gray-900"
          >
            Recent activity
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            The latest updates across your store.
          </p>
        </div>
        <Link
          to={viewAll.href}
          className="text-sm font-medium text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {viewAll.label}
        </Link>
      </div>
      <div
        role="tablist"
        aria-label="Activity type"
        className="flex gap-5 border-b border-gray-100 px-5"
      >
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            id={`home-activity-tab-${tab.toLowerCase()}`}
            aria-selected={activeTab === tab}
            aria-controls={`home-activity-panel-${tab.toLowerCase()}`}
            tabIndex={activeTab === tab ? 0 : -1}
            ref={(element) => {
              tabRefs.current[tab] = element;
            }}
            onClick={() => setSelectedTab(tab)}
            onKeyDown={(event) => navigateTabs(event, tab)}
            className={`border-b-2 pb-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              activeTab === tab
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-900'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`home-activity-panel-${activeTab.toLowerCase()}`}
        aria-labelledby={`home-activity-tab-${activeTab.toLowerCase()}`}
        tabIndex={0}
        className="focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
      >
        {activeTab === 'Requests' ? (
          !ready ? (
            <p className="p-5 text-sm text-gray-500">
              {loading
                ? 'Loading recent activity…'
                : 'Recent activity is unavailable until current requests load.'}
            </p>
          ) : requests.length === 0 ? (
            <p className="p-5 text-sm text-gray-500">
              No issue, return, or exchange activity yet.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {requests.slice(0, 5).map((request) => (
                <RequestActivity
                  key={`${request.type}-${request.id}`}
                  request={request}
                />
              ))}
            </ul>
          )
        ) : storeId ? (
          <AdditionalActivity
            key={`${storeId}-${activeTab}`}
            tab={activeTab}
            storeId={storeId}
          />
        ) : (
          <p className="p-5 text-sm text-gray-500">
            Choose a store to view recent activity.
          </p>
        )}
      </div>
    </section>
  );
};
